import React, { useState } from "react";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TruckIcon, LockKeyIcon, EnvelopeIcon } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.role === "super_admin") {
        nav("/super-admin");
      } else if (u?.role === "driver") {
        nav("/driver");
      } else if (u?.role === "mechanic") {
        nav("/jobs");
      } else {
        nav("/app");
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5">
      {/* Left: hero image */}
      <div
        className="hidden lg:flex lg:col-span-3 relative bg-slate-900"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.78), rgba(15,23,42,0.95)), url('https://images.unsplash.com/photo-1636070762406-4262052fa6ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBmbGVldCUyMGhpZ2h3YXl8ZW58MHx8fHwxNzgyNzQ2ODU1fDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="p-12 flex flex-col justify-between w-full text-white">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Rep Fleet" className="w-12 h-12 rounded-sm bg-white p-0.5 object-contain" />
            <div>
              <div className="font-display font-black text-xl leading-none">REP FLEET</div>
              <div className="font-mono text-[10px] text-blue-400 uppercase tracking-widest mt-1">Maintenance Tracker</div>
            </div>
          </div>

          <div className="max-w-xl">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-3">
              /// FLEET INTELLIGENCE
            </div>
            <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tighter leading-[1.05] mb-6">
              Every wrench turn. Every mile. Every dollar.
            </h1>
            <p className="text-slate-300 text-base leading-relaxed max-w-md">
              Track repairs, schedule services, and predict maintenance for your entire trucking fleet in one
              command center.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6 text-sm border-t border-slate-700 pt-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Trucks</div>
              <div className="font-display text-2xl font-bold mt-1">Unlimited</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Repair Logs</div>
              <div className="font-display text-2xl font-bold mt-1">Real-time</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">AI Insights</div>
              <div className="font-display text-2xl font-bold mt-1">Claude 4.5</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="lg:col-span-2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="Rep Fleet" className="w-11 h-11 rounded-sm object-contain border border-slate-200" />
            <div className="font-display font-black text-xl tracking-tighter">REP FLEET</div>
          </div>

          <div className="section-eyebrow mb-2">/// Sign in</div>
          <h2 className="font-display text-3xl font-black tracking-tighter text-slate-900 mb-2">
            Access your dashboard
          </h2>
          <p className="text-sm text-slate-600 mb-8">
            Use the credentials provided by your fleet administrator.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">
                Email
              </Label>
              <div className="relative mt-1.5">
                <EnvelopeIcon
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  required
                  placeholder="you@company.com"
                  className="pl-9 rounded-sm border-slate-300 focus:ring-blue-600 focus:ring-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">
                Password
              </Label>
              <div className="relative mt-1.5">
                <LockKeyIcon
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  required
                  placeholder="••••••••"
                  className="pl-9 rounded-sm border-slate-300 focus:ring-blue-600 focus:ring-2"
                />
              </div>
            </div>

            {err && (
              <div
                className="border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2 rounded-sm"
                data-testid="login-error"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11 font-semibold tracking-tight"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>

            <p className="text-[11px] text-slate-500 text-center leading-relaxed" data-testid="login-consent">
              By continuing you agree to our{" "}
              <Link to="/terms" className="text-blue-600 hover:underline font-medium" data-testid="login-terms-link">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" className="text-blue-600 hover:underline font-medium" data-testid="login-privacy-link">Privacy Policy</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
